import React, {
  useEffect,
  forwardRef,
  useMemo,
  useState,
  useImperativeHandle
} from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  styled,
  TextField,
  Typography
} from '@mui/material';
import {useForm, Controller} from 'react-hook-form';
import * as Yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import SelectInput from '@components/form/select-input/SelectInput';

import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {enqueueSnackbar} from 'notistack';
import EncryptUtils from 'src/utils/EncryptUtils';
import {
  getLMSResultsData,
  getSearchResultsData
} from '@shared/services/service-handlers/PrivateService';
import {useAppContext} from '@context/AppContext';
import {BASE_RECORD_ID} from 'src/routes/BaseRoute';
import {useOnlineContext} from '../../../ModulePageComponents/AdditionData/OnlineTestContext';
import {FormControlDiv} from '@modules/home/main/modules-menu/module-search-filter/ModuleSearchFilterStyle';

interface AssessmentDetailsProps {
  uiFields?: any;
  insertdata?: any;
  setDropDownData?: any;
  dropDownData?: any;
}
const StyledLabel = styled('span')(({theme}) => ({
  ...theme.typography.caption,
  fontSize: '0.9rem',
  flexShrink: 0,
  color: theme.palette.text.secondary,
  fontWeight: theme.typography.fontWeightSemiBold
}));
interface ChildFormMethods {
  submitForm: () => Promise<any>;
}
const AssessmentSetupPost = forwardRef<
  ChildFormMethods,
  AssessmentDetailsProps
>(({insertdata}, ref) => {
  const {
    appState: {applicationName}
  } = useAppContext();
  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );
  const {onlineData, setOnlineData} = useOnlineContext();
  const {uiFields, setUiFields} = useOnlineContext();
  const {dropDowndata, setDropDownData} = useOnlineContext();
  const {optionsRendered, setOptionsRendered} = useOnlineContext();
  const {screenSave, setScreenSave} = useOnlineContext();
  const {screenLoader, setScreenLoader} = useOnlineContext();
  const [assessmentTimeLimited, setassessmentTimeLimit] = useState(3);
  React.useEffect(() => {
    const initialFetching = async () => {
      if (uiFields && !optionsRendered) {
        setScreenLoader(true);
        const {sql_data} = uiFields['AssessmentSetUpForm'];
        try {
          const data: any = {
            sql_data: sql_data,
            operation: 'GETFORMOPTIONS'
          };
          const encryptRequestData = EncryptUtils.encryptedData(data);
          const result = await getLMSResultsData(applicationName, 'LMS', {
            tk: encryptRequestData
          });
          setDropDownData((prevData: any[]) => {
            const resultData = result.data; // Assuming `result.data` contains the JSON object

            const transformedData = Object.keys(resultData).map(key => ({
              Name: key,
              Values: resultData[key].map((item: any) => ({
                label: item.label,
                value: item.value
              }))
            }));

            return [...(prevData || []), ...transformedData];
          });
          setOptionsRendered(true);
          setScreenLoader(false);
        } catch (e) {
          setScreenLoader(false);
          const errMessage = getErrorMessage(e);
          enqueueSnackbar(errMessage, SnackBarConfig.getError());
        }
      }
    };
    initialFetching();
  }, [optionsRendered]);
  const createValidationSchema = (formValuess: any) => {
    const {assessment_time_limit_type_id} = formValuess;
    if (assessment_time_limit_type_id === 1) {
      return Yup.object().shape({
        name: Yup.string().required('Add the name'),
        description: Yup.string(),
        allowed_no_of_reattempts_id: Yup.number()
          .required('Maximum allowed attempts is required')
          .min(1, 'Must be at least 1'),
        allowed_reattempt_type_id: Yup.number()
          .required('Reattempt type is required')
          .min(1, 'Must be at least 1'),
        pass_percentage: Yup.number().nullable().max(100, 'Cannot exceed 100'),
        assessment_time_limit_type_id: Yup.number().required(
          'Assessment time limit is required'
        ),
        assessment_question_layout_type_id: Yup.number().required(
          'Layout preference is required'
        ),
        overallTime_in_minutes: Yup.number()
          .typeError('Time must be a number') // Ensure the value is a number
          .required('Time is required') // Field must not be empty
          .positive('Time is to be Positive')
          .moreThan(0, 'Time must be greater than 0'),
        time_per_question_in_minutes: Yup.number().nullable(),
        // assessment_question_final_score_type_id: Yup.number().required(
        //   'Final score preference is required'
        // ),
        assessment_show_marks_type_id: Yup.number().notRequired(),
        shuffle_questions_enabled: Yup.boolean(),
        showMarks: Yup.boolean()
      });
    }
    if (assessment_time_limit_type_id === 2) {
      return Yup.object().shape({
        name: Yup.string().required('Add the name'),
        description: Yup.string(),
        allowed_no_of_reattempts_id: Yup.number()
          .required('Maximum allowed attempts is required')
          .min(1, 'Must be at least 1'),
        allowed_reattempt_type_id: Yup.number()
          .required('Reattempt type is required')
          .min(1, 'Must be at least 1'),
        pass_percentage: Yup.number().nullable().max(100, 'Cannot exceed 100'),
        assessment_time_limit_type_id: Yup.number().required(
          'Assessment time limit is required'
        ),
        assessment_question_layout_type_id: Yup.number().required(
          'Layout preference is required'
        ),
        overallTime_in_minutes: Yup.number().nullable(),
        time_per_question_in_minutes: Yup.number()
          .typeError('Time must be a number') // Ensure the value is a number
          .required('Time is required') // Field must not be empty
          .moreThan(0, 'Time must be greater than 0'),

        // assessment_question_final_score_type_id: Yup.number().required(
        //   'Final score preference is required'
        // ),
        assessment_show_marks_type_id: Yup.number().notRequired(),
        shuffle_questions_enabled: Yup.boolean(),
        showMarks: Yup.boolean()
      });
    }
    return Yup.object().shape({
      name: Yup.string().required('Add the name'),
      description: Yup.string(),
      allowed_no_of_reattempts_id: Yup.number()
        .required('Maximum allowed attempts is required')
        .min(1, 'Must be at least 1'),
      allowed_reattempt_type_id: Yup.number()
        .required('Reattempt type is required')
        .min(1, 'Must be at least 1'),
      pass_percentage: Yup.number().nullable().max(100, 'Cannot exceed 100'),
      assessment_time_limit_type_id: Yup.number().required(
        'Assessment time limit is required'
      ),
      assessment_question_layout_type_id: Yup.number().required(
        'Layout preference is required'
      ),
      // assessment_question_final_score_type_id: Yup.number().required(
      //   'Final score preference is required'
      // ),
      assessment_show_marks_type_id: Yup.number().notRequired(),
      shuffle_questions_enabled: Yup.boolean(),
      time_per_question_in_minutes: Yup.number().nullable(),
      overallTime_in_minutes: Yup.number().nullable(),
      showMarks: Yup.boolean()
    });
  };
  const validationSchema = useMemo(
    () =>
      createValidationSchema({
        assessment_time_limit_type_id: assessmentTimeLimited // Default to 'No time limit'
      }),
    [assessmentTimeLimited]
  );
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: {errors, isDirty}
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      name: onlineData?.name || '',
      description: onlineData?.description || '',
      allowed_no_of_reattempts_id: onlineData?.allowed_no_of_reattempts_id || 1,
      allowed_reattempt_type_id: onlineData?.allowed_reattempt_type_id || 1,
      assessment_time_limit_type_id:
        onlineData?.assessment_time_limit_type_id || assessmentTimeLimited, // Default to 'No time limit'
      assessment_question_layout_type_id:
        onlineData?.assessment_question_layout_type_id || 1, // Default to 'One question per page'
      shuffle_questions_enabled: onlineData?.shuffle_questions_enabled || false,
      pass_percentage: onlineData?.pass_percentage || null,
      showMarks: onlineData?.assessment_show_marks_type_id ? true : false,
      time_per_question_in_minutes:
        onlineData?.time_per_question_in_minutes || null,
      overallTime_in_minutes: onlineData?.overallTime_in_minutes || null,
      assessment_show_marks_type_id: onlineData?.assessment_show_marks_type_id,
      // assessment_question_final_score_type_id:
      //   onlineData?.assessment_question_final_score_type_id || 1 // Default to 'Highest score'
    }
  });
  const showMarks = watch('showMarks');
  const assessmentTimeLimit = watch('assessment_time_limit_type_id');
  const totalAttempts = watch('allowed_no_of_reattempts_id');
  React.useEffect(() => {
    if (showMarks) {
      setValue(
        'assessment_show_marks_type_id',
        onlineData?.assessment_show_marks_type_id || 1
      ); // Set the default radio option
    } else {
      setValue('assessment_show_marks_type_id', null); // Clear the value if showMarks is false
    }
  }, [showMarks, setValue]);
  const passPercentages = watch('pass_percentage');
  React.useEffect(() => {
    const Value = watch('assessment_show_marks_type_id');
    if (
      showMarks &&
      (passPercentages === null || passPercentages === undefined)
    ) {
      if (Value == 2 || Value == 3) {
        setValue('assessment_show_marks_type_id', 1); // Set the default radio option
      }
    }
  }, [passPercentages, setValue]);
  // Update layout dynamically based on time limit
  React.useEffect(() => {
    if (assessmentTimeLimited === 2) {
      setValue('assessment_question_layout_type_id', 1); // Set to "All questions in one page"
      setValue(
        'time_per_question_in_minutes',
        onlineData?.time_per_question_in_minutes || 1
      );
      setValue('overallTime_in_minutes', null);
    }
    if (assessmentTimeLimited === 1) {
      setValue('time_per_question_in_minutes', null);
      setValue(
        'overallTime_in_minutes',
        onlineData?.overallTime_in_minutes || 1
      );
    }
    if (assessmentTimeLimited === 3) {
      setValue('time_per_question_in_minutes', null);
      setValue('overallTime_in_minutes', null);
    }
  }, [assessmentTimeLimit, assessmentTimeLimited, setValue]);
  const onSubmit = async (datavalue: any) => {
    try {
      if (onlineData?.inserted_id) {
        const operations = 'UPDATE';
        datavalue.overview_id = onlineData?.course_overview_id ||uniqueId;
        datavalue.id = onlineData.inserted_id;
        datavalue.Page = 1;
        datavalue.course_overview_id = uniqueId;
        const {sql_data} = uiFields['create_sql'];
        // datavalue.bloburls = datavalue.bloBurls[0].blobUrl;
        setScreenSave(true);
        const data: any = {
          sql_data: sql_data,
          data: datavalue,
          operation: operations
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestData
        });
        const transformedResult = result.data.map((item: any) => {
          return {
            ...item,
            // Parse JSON fields or set them to empty arrays if they are null
            ipRestrictions: item?.ipRestrictions
              ? JSON.parse(item.ipRestrictions)
              : null
            // Add any additional transformations here if needed
          };
        });
        setOnlineData(transformedResult[0]);
      } else {
        const operations = 'INSERT';
        datavalue.overview_id = onlineData?.course_overview_id ||uniqueId;
        datavalue.Page = 1;
        datavalue.course_overview_id =
          onlineData?.course_overview_id || uniqueId;
        const {sql_data} = uiFields['create_sql'];
        // datavalue.bloburls = datavalue.bloBurls[0].blobUrl;
        setScreenSave(true);
        const data: any = {
          sql_data: sql_data,
          data: datavalue,
          operation: operations
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await getLMSResultsData(applicationName, 'LMS', {
          tk: encryptRequestData
        });
        const value = result.data[0];
        setOnlineData((prevData: any) => ({
          ...prevData, // Keep existing keys
          ...value // Merge new keys from result
        }));
      }
      setScreenSave(false);

      // Send data to the server
    } catch (e) {
      setScreenSave(false);
    }
  };
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      return new Promise((resolve, reject) => {
        // First, trigger validation manually
        trigger()
          .then(isValid => {
            if (!isValid) {
              console.warn('Validation failed, form not submitted.');
              resolve(false); // Prevent submission if validation fails
              return;
            }
            if (!isDirty) {
              console.warn('Validation failed, form not submitted.');
              resolve(true); // Prevent submission if validation fails
              return;
            }
            // Now perform the actual form submission
            handleSubmit(
              async data => {
                try {
                  await onSubmit(data);
                  resolve(true);
                } catch (error) {
                  console.error('Form submission failed:', error);
                  reject(false);
                }
              },
              errors => {
                console.error('Validation errors:', errors);
                resolve(false); // Reject promise on validation error
              }
            )();
          })
          .catch(() => resolve(false)); // Catch any unexpected errors
      });
    }
  }));
  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{width: '100%', p: 3}}>
      <Card variant="outlined" sx={{padding: '1vh 1.5vh', marginBottom: '2vh'}}>
        <Typography variant="h5" sx={{mt: 2, mb: 1}}>
          Assessment Setup
        </Typography>
        <Typography variant="body2" sx={{mb: 2}}>
          Give your assessment a name and description
        </Typography>
        <div style={{borderTopColor: 'aqua', borderTopStyle: 'groove'}}>
          <Grid spacing={2} sx={{pt: 1, pb: 1, px: 2}}>
            <Grid container>
              <Grid item xs={12} sm={10} md={6}>
                <FormControl fullWidth margin="normal">
                  <StyledLabel>Name</StyledLabel>
                  <Controller
                    name="name"
                    control={control}
                    render={({field}) => (
                      <TextField
                        {...field}
                        fullWidth
                        size="small"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid item>
              <FormControl fullWidth margin="normal">
                <StyledLabel>Description</StyledLabel>
                <Controller
                  name="description"
                  control={control}
                  render={({field}) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      size="small"
                      InputProps={{
                        sx: {
                          typography: 'body2',
                          minHeight: '12vh',
                          alignItems: 'baseline'
                        }
                      }}
                      sx={{minHeight: '12vh'}}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </div>
      </Card>
      <Card variant="outlined" sx={{padding: '1vh 1.5vh', marginBottom: '2vh'}}>
        <Typography variant="h5" sx={{mt: 2, mb: 1}}>
          Assessment Preferences
        </Typography>
        <Typography variant="body2" sx={{mb: 2}}>
          Tailor the assessment to your preference
        </Typography>
        <div style={{borderTopColor: 'aqua', borderTopStyle: 'groove'}}> </div>
        <Grid container sx={{mt: 1}}>
          <Grid item xs={12} sm={10} md={6}>
            <FormControl fullWidth margin="normal">
              <Controller
                name="allowed_no_of_reattempts_id"
                control={control}
                render={({field}) => (
                  <FormControlDiv>
                    <SelectInput
                      enableDefaultOption
                      name="allowed_no_of_reattempts_id"
                      label="Maximum Allowed Attempts"
                      options={
                        dropDowndata.find(
                          (item: {Name: string}) =>
                            item.Name === 'AllowedNoofReattemptsId'
                        )?.Values || []
                      }
                      value={field.value}
                      fullWidth
                      // onOpenCallback={(event: any) =>
                      //   handleLegacyAppChange(event, 'Maximumallowedattempts')
                      // }
                      onChange={(event: any) => {
                        field.onChange(event);

                        const showMarksId = watch(
                          'assessment_show_marks_type_id'
                        );
                        if (event.target.value === 11 && showMarksId == 1) {
                          setValue('assessment_show_marks_type_id', 1);
                        } else if (
                          event.target.value === 11 &&
                          (showMarksId == 3 || showMarksId == 4)
                        ) {
                          setValue('assessment_show_marks_type_id', 5);
                        } else if (
                          event.target.value != 11 &&
                          showMarksId == 5
                        ) {
                          setValue('assessment_show_marks_type_id', 2);
                        }
                        // setMaxAttempts(Number(event.target.value));
                        // setState(prevState => ({
                        //   ...prevState,
                        //   formError: ''
                        // }));
                      }}
                      optionValueKey={'value'}
                      optionLabelKey={'label'}
                      fetchOptionsFromOnOpenTrigger
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: '30vh', // Set dropdown max height
                            overflowY: 'auto' // Enable scrolling
                          }
                        }
                      }}
                    />
                  </FormControlDiv>
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid container sx={{mt: 1}}>
          <Grid item xs={12} sm={10} md={6}>
            <FormControl fullWidth margin="normal">
              <Controller
                name="allowed_reattempt_type_id"
                control={control}
                render={({field}) => (
                  <FormControlDiv>
                    <SelectInput
                      enableDefaultOption
                      name="allowed_reattempt_type_id"
                      label="Allow Reattempts Until"
                      options={
                        dropDowndata?.find(
                          (item: {Name: string}) =>
                            item.Name === 'AllowReattemptsUntil'
                        )?.Values || []
                      }
                      value={field.value}
                      fullWidth
                      // onOpenCallback={(event: any) =>
                      //   handleLegacyAppChange(event, 'Maximumallowedattempts')
                      // }
                      onChange={(event: any) => {
                        field.onChange(event);
                        // setMaxAttempts(Number(event.target.value));
                        // setState(prevState => ({
                        //   ...prevState,
                        //   formError: ''
                        // }));
                      }}
                      optionValueKey={'value'}
                      optionLabelKey={'label'}
                      fetchOptionsFromOnOpenTrigger
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: '30vh', // Set dropdown max height
                            overflowY: 'auto' // Enable scrolling
                          }
                        }
                      }}
                    />
                  </FormControlDiv>
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
        <div style={{display: 'grid'}}>
          <FormControl
            component="fieldset"
            margin="normal"
            sx={{fontSize: '0.95rem', color: '#0c0c0c'}}>
            <Typography variant="body1" sx={{mt: 2}}>
              Assessment time limit
            </Typography>
            <Controller
              name="assessment_time_limit_type_id"
              control={control}
              render={({field}) => (
                <RadioGroup
                  row={true}
                  value={field.value}
                  onChange={(e, v) => {
                    const value = parseInt(e.target.value, 10);
                    field.onChange(value);
                    setassessmentTimeLimit(value);
                  }}>
                  {dropDowndata
                    ?.find(
                      (item: {Name: string}) =>
                        item.Name === 'AssessmentTimeLimitType'
                    )
                    ?.Values?.map(
                      (
                        option: {
                          value: number;
                          label: string;
                        },
                        index: React.Key | null | undefined
                      ) => (
                        <FormControlLabel
                          key={index} // Use a unique key for each option
                          value={option.value} // Radio value
                          control={<Radio />}
                          label={option.label} // Displayed label
                        />
                      )
                    )}
                </RadioGroup>
              )}
            />
          </FormControl>
          {assessmentTimeLimit === 2 && (
            <Grid container>
              <Grid item xs={12} sm={10} md={6}>
                <FormControl fullWidth margin="normal">
                  <Controller
                    name="time_per_question_in_minutes"
                    control={control}
                    render={({field}) => (
                      <TextField
                        {...field}
                        type="number"
                        focused
                        fullWidth={false}
                        label="Time in minutes"
                        inputProps={{min: 0}}
                        error={!!errors.time_per_question_in_minutes}
                        helperText={
                          errors.time_per_question_in_minutes?.message
                        }
                        size="small"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          )}
          {assessmentTimeLimit === 1 && (
            <Grid container>
              <Grid item xs={12} sm={10} md={6}>
                <FormControl fullWidth margin="normal">
                  <Controller
                    name="overallTime_in_minutes"
                    control={control}
                    render={({field}) => (
                      <TextField
                        {...field}
                        type="number"
                        focused
                        fullWidth={false}
                        label="Time in minutes"
                        error={!!errors.overallTime_in_minutes}
                        helperText={errors.overallTime_in_minutes?.message}
                        size="small"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          )}
        </div>
        <div>
          <Typography variant="body1" sx={{mt: 2}}>
            Layout
          </Typography>
          <Controller
            name="assessment_question_layout_type_id"
            control={control}
            render={({field}) => (
              <RadioGroup row={true} {...field}>
                {dropDowndata
                  ?.find(
                    (item: {Name: string}) =>
                      item.Name === 'AssessmentQuestionLayoutType'
                  )
                  ?.Values?.map(
                    (
                      option: {
                        value: number;
                        label: string;
                      },
                      index: React.Key | null | undefined
                    ) => (
                      <FormControlLabel
                        key={index} // Use a unique key for each option
                        value={option.value} // Radio value
                        control={<Radio />}
                        disabled={
                          assessmentTimeLimit === 2 &&
                          option.value === assessmentTimeLimit
                        }
                        label={option.label} // Displayed label
                      />
                    )
                  )}
              </RadioGroup>
            )}
          />

          <Controller
            name="shuffle_questions_enabled"
            control={control}
            render={({field}) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Shuffle questions"
              />
            )}
          />
        </div>
      </Card>
      <Card variant="outlined" sx={{padding: '1vh 1.5vh'}}>
        <Typography variant="h6" sx={{mt: 4}}>
          Score Preferences
        </Typography>
        <Grid container>
          <Grid item xs={12} sm={10} md={6}>
            <Controller
              name="pass_percentage"
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Pass percentage"
                  type="number"
                  error={!!errors.pass_percentage}
                  helperText={errors.pass_percentage?.message}
                  margin="normal"
                  onChange={e => {
                    const value = e.target.value;
                    // Only allow numbers
                    if (value === '' || /^\d+$/.test(value)) {
                      field.onChange(value === '' ? null : parseInt(value, 10));
                      const showMarksId = watch(
                        'assessment_show_marks_type_id'
                      );
                      const attempts = watch('allowed_no_of_reattempts_id');
                      if (Number(attempts) != 11 && showMarksId == 5) {
                        setValue('assessment_show_marks_type_id', 2);
                      } // Pass `null` for empty, else cast to number
                    }
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        <Controller
          name="showMarks"
          control={control}
          render={({field}) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value} />}
              label="Show marks to learner"
            />
          )}
        />

        {showMarks && (
          <Controller
            name="assessment_show_marks_type_id"
            control={control}
            render={({field}) => {
              const passPercentage = watch('pass_percentage'); // Watch the value of pass_percentage

              // Determine which options to show based on pass_percentage
              const allOptions = dropDowndata?.find(
                (item: {Name: string}) =>
                  item.Name === 'AssessmentShowMarksType'
              )?.Values;
              const filteredOptions =
                passPercentage === null || passPercentage === undefined
                  ? totalAttempts === 11
                    ? allOptions?.filter(
                        (_: any, index: number) => index === 0 || index === 4 // Show only the 1st and 5th options
                      )
                    : allOptions?.filter(
                        (_: any, index: number) =>
                          index === 0 || index === 3 || index === 4 // Show only the 1st and 4th options
                      )
                  : totalAttempts === 11
                    ? allOptions?.filter(
                        (_: any, index: number) =>
                          index === 0 || index === 1 || index === 4 // Show only the 1st and 5th options
                      )
                    : allOptions?.filter(
                        (_: any, index: number) => index != 4 // Show only the 1st and 4th options
                      ); // Show all options if pass_percentage is not null

              return (
                <RadioGroup {...field}>
                  {filteredOptions &&
                    filteredOptions?.map(
                      (
                        option: {
                          value: number;
                          label: string;
                        },
                        index: React.Key | null | undefined
                      ) => (
                        <FormControlLabel
                          key={index} // Use a unique key for each option
                          value={option.value} // Radio value
                          control={<Radio />}
                          label={option.label} // Displayed label
                        />
                      )
                    )}
                </RadioGroup>
              );
            }}
          />
        )}

        {/* <Typography variant="body1" sx={{mt: 2}}>
          Final Score
        </Typography>
        <Controller
          name="assessment_question_final_score_type_id"
          control={control}
          render={({field}) => (
            <RadioGroup row={true} {...field}>
              {dropDownData
                ?.find(
                  (item: {Name: string}) =>
                    item.Name === 'AssessmentQuestionFinalScoreType'
                )
                ?.Values?.map(
                  (
                    option: {
                      value: number;
                      label: string;
                    },
                    index: React.Key | null | undefined
                  ) => (
                    <FormControlLabel
                      key={index} // Use a unique key for each option
                      value={option.value} // Radio value
                      control={<Radio />}
                      label={option.label} // Displayed label
                    />
                  )
                )}
            </RadioGroup>
          )}
        />
        {errors.assessment_question_final_score_type_id && (
          <Typography color="error">
            {errors.assessment_question_final_score_type_id.message}
          </Typography>
        )} */}
      </Card>
    </Box>
  );
});
export default AssessmentSetupPost;
